import React from 'react';
import classnames from 'classnames';
import { gikClassPrefix } from '@gik/core/constants';
import { useProducts } from '../../api/products';
import { Decoder, FrameBone, Image, LoadingSpinner, ParagraphBone } from '@gik/ui';
import { CartItem } from '../..';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import bemBlock from '@gik/core/utils/bemBlock';
import { ShippingDetails } from '@gik/core/models/gik/Order';
import { formatCurrency } from '@gik/core/utils/format';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import { useInkind } from '@gik/api/inkinds/inkind';
import ContactPageOrganizersForAddress from '@gik/calendar';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';

export interface ProductListProps extends React.HTMLAttributes<HTMLDivElement> {
  recipientName: string;
  cart: CartItem[];
  skeleton?: boolean;
  skeletonItems?: number;
  shipping: ShippingDetails;
  isShippingStep?: boolean;
  inkindRouteId?: string;
}

const skelBem = bemBlock(`product-list-skeleton`);
const Skeleton = ({ skeletonItems }: Partial<ProductListProps>) => (
  <div className={skelBem()}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={3} className={skelBem('title')} />
            <ParagraphBone words={30} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </div>
);

const blockName = `${gikClassPrefix}-product-list`;
function ProductList({
  className,
  cart,
  recipientName,
  shipping,
  inkindRouteId,
  isShippingStep,
  ...otherProps
}: ProductListProps): React.ReactElement {
  const productIds = cart ? cart.map(item => item.productId) : [];
  const { data: products, error: productError } = useProducts(productIds);
  const { data: inkindPage, error: inkindError } = useInkind(inkindRouteId);
  const isSmDown = useBreakpoint(Breakpoint.SM_DOWN);

  const isLoading = !products;
  const isError = isLoading && (productError || inkindError);

  if (isLoading) return <LoadingSpinner />;

  const blockClasses = classnames([blockName, className || '']);

  const checkoutType = products[0].checkoutType;

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  const isShowContactPageOrganizers = isShippingStep && inkindPage && !isInkindPageRecipientInfoComplete(inkindPage);

  return (
    <div>
      {products.map(product => {
        const cartItem = cart.find(item => item.productId === product.id);
        let image = null;
        if (product.images?.length) image = product.images[0];

        return (
          <div key={product.id} className={blockClasses} {...otherProps}>
            <header>
              <figure>
                <Image src={image} />
              </figure>
              <main>
                {!cartItem && <p>Product not found...</p>}
                {cartItem && (
                  <>
                    <span className={`${blockName}__title`}>
                      <Decoder text={`${formatCurrency(cartItem.price)} ${product.name}`} />
                    </span>
                    {checkoutType === 'gik-premium' && (
                      <p>
                        {' '}
                        For <Decoder text={inkindPage.title} />
                      </p>
                    )}
                    {recipientName && checkoutType !== 'gik-premium' && (
                      <>
                        {checkoutType === 'tango-card' ? (
                          <p>
                            Your Gift Card will be delivered electronically to&nbsp;
                            <strong>
                              <Decoder text={recipientName} />
                            </strong>{' '}
                            at <strong>{shipping.email}</strong>
                          </p>
                        ) : checkoutType === 'giftbox' ? (
                          <p>
                            Your Gift Box will be mailed to&nbsp;
                            <strong>
                              <Decoder text={recipientName} />
                            </strong>{' '}
                            {shipping.city && shipping.state && (
                              <>
                                in{' '}
                                <strong>
                                  {shipping.city}, {shipping.state}
                                </strong>
                              </>
                            )}
                          </p>
                        ) : (
                          <p>
                            Your gift will be sent to&nbsp;
                            <strong>
                              <Decoder text={recipientName} />
                            </strong>{' '}
                            in{' '}
                            <strong>
                              {shipping.city}, {shipping.state}
                            </strong>
                          </p>
                        )}
                      </>
                    )}
                    {!isSmDown && isShowContactPageOrganizers && (
                      <ContactPageOrganizersForAddress inkindRouteId={inkindRouteId} />
                    )}
                  </>
                )}
              </main>
            </header>
            {isSmDown && isShowContactPageOrganizers && (
              <ContactPageOrganizersForAddress inkindRouteId={inkindRouteId} variant="centered" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default withSuspense(ProductList, Skeleton);
